import { useState, useEffect, useContext, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { DesktopSidebar, MobileSidebar } from "./Sidebar";
import { Navbar } from "./Navbar";
import AuthContext from "../../auth/AuthContext";
import Breadcrumbs from "../breadcrumps/Breadcrumbs";
import MenuService from "../../services/MenuService";
import AlertService from "../../services/AlertService";
import Icon from "./Icon";
import { useLabActivity } from "../pages/cyber-range/LabActivityContext";
// import useNetworkMonitor from "../pages/error/NetworkMonitor";
// import NetworkAlert from "../pages/error/NetworkAlert";




const MainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const authContext = useContext(AuthContext);
  const [formattedMenus, setFormattedMenus] = useState([]);
  const queryParams = new URLSearchParams(location.search);
  const isVmMode = queryParams.get("vmMode") === "true";
  const [filters, setFilters] = useState({});
  // const { isSlow, isOffline } = useNetworkMonitor();

  const {
    isLabRunning,
    remainingSeconds,
    idleStage,
    showIdleWarning,
    warningSeconds,
  } = useLabActivity();




const getSubItems = useCallback((menu) => {
  if (menu.hiddenMenu) return null;

  let result = {
    name: menu.displayText,
    path: menu.routerLink,
    icon: (
      <Icon
        className="me-2 sidebar-icons"
        alt={menu.displayText}
        base64={menu.iconName}
      />
    ),
  };

  if (menu.child && menu.child.length > 0) {
    result.subItems = menu.child
      .map((subItem) => getSubItems(subItem))
      .filter(Boolean);
  }

  return result;
}, []);

  const loadMenus = useCallback(async (signal) => {
    try {
      const data = await MenuService.GetMenus(
        authContext.userId,
        authContext,
        signal
      );

      if (signal?.aborted) return;
      if (!data) return;
       if (data.isSuccess) {
  let tempFormattedMenus = data.data
    .map(menu => getSubItems(menu))
    .filter(Boolean);

        setFormattedMenus(tempFormattedMenus);
      } else {
        AlertService.error(data.errorMessage);
        setFormattedMenus([]);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Menu load failed:", error);
      setFormattedMenus([]);
    }
  }, [setFormattedMenus, getSubItems, authContext]);

  useEffect(() => {
    const controller = new AbortController();
    loadMenus(controller.signal);
    return () => controller.abort();
  }, [loadMenus]);

  // MFA redirects
  useEffect(() => {
    if (authContext.mfaSetupEnabled && !authContext.mfaSetupCompleted) {
      navigate("/qrcode");
      return;
    }
    if (
      authContext.mfaSetupEnabled &&
      authContext.mfaSetupCompleted &&
      !authContext.mfaVerified
    ) {
      navigate("/mfa");
      return;
    }
  }, [location, authContext, navigate]);

  // Resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isSidebarOpen ? "hidden" : "auto";
  }, [isSidebarOpen]);

  const currentPath = location.pathname;

  const setActiveTab = (path) => {
    navigate(`/${path}`);
  };

  const sidebarItems = () => {
    return formattedMenus.length > 0 ? [...formattedMenus] : [];
  };

  const hidePaths = [
    "/profile",
    "/profile/edit",
    "/profile/update-security",
    "/profile/change-password",
    "/profile/configure-mfa-otp",
  ];

  const shouldHideSidebar =
    hidePaths.includes(currentPath) ||
    currentPath.startsWith("/configureqr/") ||
    (location.pathname === "/bay" && isVmMode);

  const shouldHideNavbar = location.pathname === "/bay" && isVmMode;

  return (
    <>
      <div className="main-layout">
{/* <NetworkAlert 
  isSlow={isSlow} 
  isOffline={isOffline} 
/> */}
       

        {!shouldHideNavbar && (
          <Navbar
            isDesktop={isDesktop}
            screenWidth={screenWidth}
            setSidebarOpen={setSidebarOpen}
            isSidebarOpen={isSidebarOpen}
            shouldHideSidebar={shouldHideSidebar}
            isLabRunning={isLabRunning}
            remainingSeconds={remainingSeconds}
            showIdleWarning={showIdleWarning}
            idleStage={idleStage}
            warningSeconds={warningSeconds}
          />
        )}

        <div className="d-flex" style={{ paddingTop: "67px" }}>
           {/* Desktop Sidebar - Normal pages */}
          {!shouldHideSidebar && isDesktop && (
            <DesktopSidebar
              items={sidebarItems()}
              currentPath={currentPath}
              setActiveTab={setActiveTab}
              screenWidth={screenWidth}
            />
          )}

        {/* Mobile Sidebar */}
          {!shouldHideSidebar && !isDesktop && isSidebarOpen && (
            <MobileSidebar
              setSidebarOpen={setSidebarOpen}
              items={sidebarItems()}
              currentPath={currentPath}
              setActiveTab={setActiveTab}
            />
          )}

          <div
            className="flex-grow-1 main-content d-flex"
            style={{
              marginLeft: shouldHideSidebar
                ? "0"
                : isDesktop
                ? "clamp(210px, 16vw, 282px)"
                : "0",
              height: "calc(100dvh - 67px)",
              overflow: "hidden",
              transition: "margin-left 0.4s ease",
            }}
          >


             {/* Main Content */}
            <div
              className="flex-grow-1 mt-lg-2 layout-scroll-no-buttons"
              style={{
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
              }}
            >
              <div style={{ flexShrink: 0 }}>
                {!shouldHideSidebar && <Breadcrumbs />}
              </div>
              <div
                className="layout-scroll-no-buttons pb-3 pb-xl-0 ps-xl-1 pe-xl-1"
                style={{
                  flexGrow: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  overscrollBehavior: "none",
                }}
              >
                <Outlet context={{ filters }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MainLayout;